import React, { useState, useEffect, useRef } from "react";
import arrowLeft from "../../assets/arrow-left.svg";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Form,
  Button,
  Image,
  Dropdown,
  DropdownDivider,
  Overlay,
  Popover,
  Row,
  Col,
  Pagination,
  Table,
  InputGroup,
} from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAppContext } from "../../AppContext";
import deleteIcon from "../../assets/delete.svg";
import { FaTrash } from "react-icons/fa";
import productRoute from "../../services/productRoute";
import FilerobotImageEditor, { TABS, TOOLS } from "react-filerobot-image-editor";
import edit from "../../assets/edit.svg";
import Select, { components } from "react-select";
import InstructionModal from "../../components/Modal/InstructionModal";
import { faChevronDown, faChevronUp, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InputGroupText from "react-bootstrap/esm/InputGroupText";

const AddProduct = () => {
  const navigate = useNavigate();
  const { showToast } = useAppContext();
  const {
    getProductCategory,
    filterByCategory,
    addProduct,
    getShoppingInstructions,
    generateProductDescription,
  } = productRoute();
  const [loading, setLoading] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const typingDelay = useRef(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [productData, setProductData] = useState({
    productName: "",
    productSize: "",
    productDescription: "",
    weightValue: "",
    productQuantity: "",
    seasonStatus: "",
    productPrice: "",
    productCategory: [],
    productStockLevel: "",
    productImage: null,
    shoppingInstructions: [],
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    alternativeDescription: "",
  });
  const [showInstructionModal, setShowInstructionModal] = useState(false);

  // State for managing new instruction input fields
  const [newInstructionName, setNewInstructionName] = useState("");
  const [newInstructionAmount, setNewInstructionAmount] = useState("");

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"], // toggled buttons
      ["blockquote"],

      [{ list: "ordered" }, { list: "bullet" }], // lists
      [{ script: "sub" }, { script: "super" }], // superscript/subscript
      [{ indent: "-1" }, { indent: "+1" }], // indent

      [{ header: [1, 2, 3, 4, 5, 6, false] }],

      [{ color: [] }, { background: [] }], // dropdown with defaults
      [{ font: [] }],
      [{ align: [] }],

      ["clean"], // remove formatting
      ["link", "image", "video"], // link and image, video
    ],
  };

  useEffect(() => {
    getProductCategory(setLoading, showToast, setCategories);
  }, []);
  useEffect(() => {
    getShoppingInstructions(setLoading, showToast, setInstructions);
  }, []);

  const categoryOptions = categories
    .filter((category) => category.status === "active") // Filter only active categories
    .map((category) => ({
      value: category._id,
      label: category.categoryName,
    }));

  const getSelectedCategoryNames = () => {
    const selected = categoryOptions.filter((opt) =>
      productData.productCategory.includes(opt.value)
    );
    return selected.map((cat) => cat.label).join(", ");
  };

  const triggerDescriptionGeneration = async () => {
    const name = productData.productName.trim();
    const categoryName = getSelectedCategoryNames();

    // ✅ Require BOTH
    if (!name || productData?.productCategory?.length === 0) {
      showToast({ toastText: "Please provide product name and category", isSucess: false });
      return;
    }

    try {
      const res = await generateProductDescription(
        {
          productName: name,
          categoryName: categoryName, // ✅ passed to backend
        },
        setGenLoading,
        showToast
      );

      setProductData((prev) => ({
        ...prev,

        metaTitle: res.meta_title || "",
        productDescription: res.description_html || "",
        metaDescription: res.meta_description || "",
        metaKeywords: Array.isArray(res.keywords) ? res.keywords.join(", ") : "",
        alternativeDescription: res.meta_description || "",
      }));
    } catch (err) {
      console.error("Generation failed:", err);
    }
  };

  useEffect(() => {
    if (!productData.productName.trim()) return;
    if (!productData.productCategory?.length) return;

    const delay = setTimeout(() => {
      triggerDescriptionGeneration();
    }, 800);

    return () => clearTimeout(delay);
  }, [productData.productName, productData.productCategory]);

  // Add this function inside your component
  const handlePaste = () => {
    if (productData.alternativeDescription) {
      setProductData((prev) => ({
        ...prev,
        productDescription: prev.alternativeDescription, // set alt description as main description
      }));
    } else if (productData.metaDescription) {
      setProductData((prev) => ({
        ...prev,
        productDescription: prev.metaDescription, // set meta description as main description
      }));
    } else {
      showToast({
        toastText: "No generated description available to paste.",
        isSuccess: false,
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setProductData((prev) => ({ ...prev, productDescription: value }));
  };

  const handleImageChange = (event) => {
    const MAX_SIZE = 3 * 1024 * 1024;
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/") && file.size <= MAX_SIZE) {
      setProductData((prev) => ({ ...prev, productImage: file }));
      // setEditorOpen(true);
    } else {
      showToast({
        toastText: "Invalid file. Please upload a non-SVG image file up to 10MB.",
        isSuccess: false,
      });
    }
  };

  const handleDeleteImage = () => {
    setProductData((prev) => ({ ...prev, productImage: null }));
  };

  const isFormValid = () => {
    return (
      productData?.productName &&
      productData?.productSize &&
      productData?.productQuantity &&
      productData?.productPrice &&
      productData?.productCategory.length > 0 &&
      productData?.productStockLevel &&
      productData?.productImage
    );
  };
  const stripHtml = (html) => {
    try {
      const temporalDivElement = document.createElement("div");
      temporalDivElement.innerHTML = html;
      return temporalDivElement.textContent || temporalDivElement.innerText || "";
    } catch (error) {
      console.error("Error stripping HTML:", error);
      return ""; // Return empty string on error
    }
  };

  const handleSave = (e) => {
    e.preventDefault();

    const plainTextDescription = stripHtml(productData.productDescription);
    const countWords = (text) => {
      if (!text) return 0;
      return text
        .trim()
        .split(/\s+/) // split by one or more spaces
        .filter(Boolean).length;
    };

    // Word count validation
    const descriptionWordCount = countWords(plainTextDescription);
    const metaTitleWordCount = countWords(productData.metaTitle);
    const metaDescriptionWordCount = countWords(stripHtml(productData.metaDescription));

    if (
      productData?.productDescription &&
      (descriptionWordCount < 8 || descriptionWordCount > 400)
    ) {
      showToast({
        toastText: "Product description must be between 8 and 400 words.",
        isSuccess: false,
      });
      return;
    }

    if (productData?.metaTitle && (metaTitleWordCount < 8 || metaTitleWordCount > 60)) {
      showToast({
        toastText: "Meta title must be between 8 and 60 words.",
        isSuccess: false,
      });
      return;
    }

    if (
      productData?.metaDescription &&
      (metaDescriptionWordCount < 8 || metaDescriptionWordCount > 155)
    ) {
      showToast({
        toastText: "Meta description must be between 8 and 155 words.",
        isSuccess: false,
      });
      return;
    }

    const formData = new FormData();
    formData.append("productName", productData.productName);
    formData.append("size", productData.productSize);
    if (productData.weightValue !== null && productData.weightValue !== "") {
      formData.append("weightValue", productData.weightValue);
    }
    formData.append("quantityAvailable", productData.productQuantity);
    formData.append("price", productData.productPrice);
    productData.productCategory.forEach((category) => {
      formData.append("categories", category);
    });
    // formData.append('categories', JSON.stringify(productData.productCategory)); // Ensure this is the category ID
    formData.append("stockLevelAlert", productData.productStockLevel);
    formData.append("productImage", productData.productImage);

    if (
      productData.seasonStatus !== null &&
      productData.seasonStatus !== "" &&
      productData.seasonStatus !== undefined
    ) {
      const inSeason = productData.seasonStatus === "In Season";
      formData.append("inSeason", inSeason);
    }
    if (
      productData.productDescription !== null &&
      productData.productDescription !== "" &&
      productData.productDescription !== undefined
    ) {
      formData.append("description", plainTextDescription);
    }
    if (productData.shoppingInstructions?.length > 0) {
      productData.shoppingInstructions.forEach((instruction, index) => {
        formData.append(`shoppingInstructions[${index}]`, instruction);
      });
    }

    // Append meta fields
    if (productData.metaTitle) {
      formData.append("metaTitle", productData.metaTitle);
    }

    if (productData.metaDescription) {
      formData.append("metaDescription", stripHtml(productData.metaDescription));
    }

    if (productData.metaKeywords) {
      // Convert comma-separated keywords to array and append each
      const keywordsArray = productData.metaKeywords
        .split(",")
        .map((kw) => kw.trim())
        .filter((kw) => kw); // remove empty strings
      keywordsArray.forEach((kw) => formData.append("metaKeywords[]", kw));
    }

    addProduct(formData, setLoading, showToast);
  };

  const CustomMenuCategoryList = (props) => {
    return <components.MenuList {...props}>{props.children}</components.MenuList>;
  };
  const options = instructions.map((instruction) => ({
    value: instruction?.id,
    label: instruction?.name,
  }));

  // Custom MenuList to add "Add Shopping Instruction"
  const CustomMenuList = (props) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            borderTop: "1px solid #ddd",
            color: "#469B12",
          }}
          onClick={() => setShowInstructionModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Shopping Instruction
        </div>
      </components.MenuList>
    );
  };

  return (
    <div className="">
      <div className="d-flex gap-3 align-items-center header-admin  py-4 pe-5 mb-4 ps-4">
        <img src={arrowLeft} onClick={handleBack} alt="arrow" />
        <h3 style={{ color: "#344054" }} className="my-auto">
          Add Product
        </h3>
      </div>

      <div className=" pe-5 ps-4 ">
        {loading ? (
          <div
            className="justify-content-center align-items-center text-center"
            style={{ padding: "100px" }}
          >
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form onSubmit={handleSave}>
            <Row className="">
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Product Name <sup className="text-danger">*</sup>
                </p>
                <Form.Control
                  type="text"
                  name="productName"
                  value={productData.productName}
                  onChange={(e) => {
                    const value = e.target.value;

                    setProductData((prev) => ({
                      ...prev,
                      productName: value,
                    }));

                    // Clear previous debounce timer
                    if (typingDelay.current) {
                      clearTimeout(typingDelay.current);
                    }

                    // ❌ Stop if name empty OR no category selected
                    if (!value.trim() || !productData.productCategory?.length) return;

                    // ✅ Debounce auto-generate (1s)
                    typingDelay.current = setTimeout(() => {
                      triggerDescriptionGeneration();
                    }, 1000);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();

                      // ✅ ALSO check category here
                      if (!productData.productName.trim() || !productData.productCategory?.length)
                        return;

                      triggerDescriptionGeneration();
                    }
                  }}
                  required
                />
              </Col>

              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  {" "}
                  Size <sup className="text-danger">*</sup>
                </p>
                <Form.Control
                  type="text"
                  name="productSize"
                  value={productData.productSize}
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Weight Value:
                </p>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Enter weight"
                    value={productData?.weightValue}
                    onChange={(event) => {
                      let value = event.target.value;

                      // Allow only digits and a single decimal point
                      if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                        setProductData({ ...productData, weightValue: value });
                      }
                    }}
                  />

                  <InputGroupText>KG</InputGroupText>
                </InputGroup>
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Category <sup className="text-danger">*</sup>
                </p>
                <Select
                  isMulti
                  options={categoryOptions}
                  onChange={(selectedOptions) => {
                    const selectedValues = selectedOptions.map((opt) => opt.value);

                    setProductData((prev) => ({
                      ...prev,
                      productCategory: selectedValues,
                    }));
                  }}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 2000, // Ensure the dropdown has a high z-index
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      zIndex: 2000, // Ensure the dropdown options have a high z-index
                    }),
                  }}
                  components={{
                    MenuList: CustomMenuCategoryList,
                  }}
                />
              </Col>

              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Price <sup className="text-danger">*</sup>
                </p>
                <Form.Control
                  type="text"
                  name="productPrice"
                  value={productData.productPrice}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setProductData({ ...productData, productPrice: numericValue });
                  }}
                  required
                />
              </Col>

              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Quantity <sup className="text-danger">*</sup>
                </p>
                <Form.Control
                  type="text"
                  name="productQuantity"
                  value={productData.productQuantity}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setProductData({ ...productData, productQuantity: numericValue });
                  }}
                  required
                />
              </Col>
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Stock Level Alert <sup className="text-danger">*</sup>
                </p>
                <Form.Control
                  type="text"
                  name="productPrice"
                  value={productData.productStockLevel}
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "");
                    setProductData({ ...productData, productStockLevel: numericValue });
                  }}
                  required
                />
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Season Status:
                </p>
                <InputGroup>
                  <Form.Select
                    type="text"
                    name="seasonStatus"
                    value={productData.seasonStatus}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Select status
                    </option>

                    <option value="In Season">In Season</option>
                    <option value="Off Season">Off Season</option>
                  </Form.Select>
                  {productData.seasonStatus && (
                    <Button
                      variant="secondary"
                      onClick={() => setProductData({ ...productData, seasonStatus: "" })}
                      className=""
                    >
                      X
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col xs={6} className="ps-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Shopping Instructions
                </p>
                <Select
                  isMulti
                  options={options}
                  onChange={(selectedOptions) => {
                    const selectedValues = selectedOptions.map((option) => option.value);
                    setProductData((prev) => ({
                      ...prev,
                      shoppingInstructions: selectedValues,
                    }));
                  }}
                  noOptionsMessage={() => "No instructions available"}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 2000, // Ensure the dropdown has a high z-index
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      zIndex: 2000, // Ensure the dropdown options have a high z-index
                    }),
                  }}
                  components={{
                    MenuList: CustomMenuList,
                  }}
                />
              </Col>
              <Col xs={6} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Meta Title{" "}
                  {genLoading && (
                    <span className="spinner-border spinner-border-sm text-success"></span>
                  )}
                </p>
                <Form.Control
                  type="text"
                  name="metaTitle"
                  value={productData.metaTitle}
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col xs={12} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Product Description:{" "}
                  {productData.alternativeDescription && (
                    <span
                      style={{ cursor: "pointer", textDecoration: "underline", color: "#469B12" }}
                      onClick={handlePaste}
                      className="my-3"
                    >
                      Paste generated description
                    </span>
                  )}
                </p>
                <ReactQuill
                  value={productData.productDescription}
                  onChange={handleDescriptionChange}
                  modules={modules}
                />
              </Col>
              <Col xs={12} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Meta Keywords{" "}
                  {genLoading && (
                    <span className="spinner-border spinner-border-sm text-success"></span>
                  )}
                </p>
                {(!productData.productName.trim() || !productData.productCategory?.length) && (
                  <p className="text-danger mt-1" style={{ fontSize: "13px" }}>
                    Add product name and category to enable meta description.
                  </p>
                )}

                <textarea
                  rows={5}
                  name="metaKeywords"
                  value={productData.metaKeywords}
                  onChange={handleChange}
                  readOnly={!productData.productName.trim()}
                  placeholder="Auto filled keywords..."
                  className="form-control"
                  style={{ width: "100%", resize: "none" }}
                />
              </Col>
              <Col xs={12} className="pe-3 mb-4">
                <p style={{ color: "#333333", fontWeight: "500" }} className="pb-0 mb-1">
                  Meta Description:{" "}
                  {!genLoading && productData.metaDescription && (
                    <span
                      style={{ cursor: "pointer", textDecoration: "underline", color: "#469B12" }}
                      onClick={() => triggerDescriptionGeneration()}
                      className="my-3"
                    >
                      Regenerate
                    </span>
                  )}
                  {genLoading && (
                    <span className="spinner-border spinner-border-sm text-success"></span>
                  )}
                </p>
                {(!productData.productName.trim() || !productData.productCategory?.length) && (
                  <p className="text-danger mt-1" style={{ fontSize: "13px" }}>
                    Add product name and category to enable meta description.
                  </p>
                )}

                <ReactQuill
                  value={productData.metaDescription}
                  onChange={(value) =>
                    setProductData((prev) => ({ ...prev, metaDescription: value }))
                  }
                  modules={modules}
                  readOnly={!productData.productName.trim()}
                />
              </Col>
            </Row>

            <div className="my-4">
              {productData.productImage ? (
                <>
                  <Button className="mb-4">Attached Image</Button>
                  <div className="d-flex  gap-3">
                    <img
                      src={URL.createObjectURL(productData.productImage)}
                      onLoad={() => URL.revokeObjectURL(productData.productImage)}
                      alt="Product"
                      style={{ width: "100px", height: "100px" }}
                    />
                    <div className="d-flex gap-2 align-items-end">
                      {/* <img src={edit} onClick={openImageEditor} style={{ width: '24px', height: '24px', cursor: 'pointer',}} />  */}
                      <img
                        src={deleteIcon}
                        onClick={handleDeleteImage}
                        style={{ width: "24px", height: "24px", cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Form.Control
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    style={{ display: "none" }}
                    id="fileInput"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => document.getElementById("fileInput").click()}
                    style={{ backgroundColor: "#CCCDCF", border: "none", borderRadius: "4px" }}
                    className="px-4"
                  >
                    Upload Product Image<sup className="text-danger">*</sup>
                  </Button>
                </>
              )}
            </div>

            <div className="pt-3 my-3 d-flex justify-content-end">
              <Button
                className="px-5 py-2"
                disabled={!isFormValid()}
                style={{
                  backgroundColor: isFormValid() ? " #469B12" : "rgba(70, 155, 18, 0.2)",
                  color: "#F6F6F6",
                  boxShadow: "0px 4px 4px 0px #0E35BF40",
                }}
                type="submit"
              >
                Save
              </Button>
            </div>
          </Form>
        )}

        {/* {editorOpen && productData.productImage && (

     
        <Modal show={editorOpen} onHide={closeImageEditor} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Image</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FilerobotImageEditor
                        source={URL.createObjectURL(productData.productImage)}
                        onSave={onImageSave}
                        onClose={closeImageEditor}
                    />
                </Modal.Body>
            </Modal>
        )}
       */}
      </div>
      <InstructionModal
        showInstructionModal={showInstructionModal}
        setShowInstructionModal={setShowInstructionModal}
      />
    </div>
  );
};

export default AddProduct;
